import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, onSnapshot, doc, updateDoc, query, orderBy } from "firebase/firestore";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";
import { enqueueSnackbar } from "notistack";
import { auth } from "../firebaseConfig";


import {
  addDoc, Timestamp,
} from "firebase/firestore";
// ─── Constants ────────────────────────────────────────────────────────────────

const TYPES = {
  Flight: { icon: "✈", color: "#2563eb", bg: "#eff6ff" },
  Hotel: { icon: "🏨", color: "#0891b2", bg: "#ecfeff" },
  Transport: { icon: "🚐", color: "#7c3aed", bg: "#f5f3ff" },
  Visa: { icon: "🛂", color: "#059669", bg: "#ecfdf5" },
};

const URGENCY = {
  "Easy": { color: "#16a34a", bg: "#f0fdf4", dot: "#22c55e" },
  "High Alert": { color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
};



const SUPPLIERS = {
  HOTEL: [
    "Aden", "Al Multazam", "Al Subee", "Al Sabir", "Anjum Hotel", "Arabian",
    "Asdaf", "Aston", "Rowa", "B2B Bookings", "Basma", "Daniyal", "Emaar UK",
    "Emerging Travel UK LTD", "EmtStays", "First Quest", "Fundaq",
    "Hassan Kamran Travel", "Heavan Travel & Tours", "Holiday Me", "Jhelum Inn",
    "Muhabiya", "Ratehawk", "Rezlive", "ROD", "Saja Al Madinah Hotel",
    "Stuba", "TBO", "Wirgan Aziziah", "Zaha Al Munawara","Akbar Travel",
  ],

  FLIGHT: [
    "Masterfare", "Brightsun", "Polani", "Air Arabia", "Air Asia", "Al Qemah",
    "British Airways", "EasyJet", "FlyNas", "Hassan Kamran Travel",
    "Heavan Travel & Tours", "National Air Service", "NO1LOUNGES", "Panama",
    "Pegasus", "Ryan Air","Akbar Travel", "AeroGlobe","Citibond","Sasta Ticket", "Visa Club Travel", "Wizz Air", "Profine Travel",
  ],

  VISA: [
    "Arabian", "Asdaf", "Asif Sahab", "AzeriCard", "Basma", "GM Travel",
    "Hassan Kamran Travel", "Heavan Travel & Tours", "Ibn e Mukhtar", "MJ Dreams",
    "Noor Taiba", "Profine Travel", "ROD Travel", "Turkey Visa Portal",
    "Visa Club", "Visit Saudi", "Yet Too Explore & Tours", "Nadra","Vietnam Visa"
  ],

  TRANSPORT: [
    "Al Tawfeeq", "Arabian", "Basma", "GM Travel", "Hassan Kamran Travel",
    "HHR", "Ibn e Mukhtar", "Mozio", "Noor Taiba", "TBO", "Heavan Travel & Tours",
  ]
};

const getSupplierList = (type) => {
  switch (type) {
    case "Flight": return SUPPLIERS.FLIGHT;
    case "Hotel": return SUPPLIERS.HOTEL;
    case "Visa": return SUPPLIERS.VISA;
    case "Transport": return SUPPLIERS.TRANSPORT;
    default: return [];
  }
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.6; transform: scale(0.85); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// ─── Global ───────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  font-family: 'DM Sans', sans-serif;
  padding: 36px 24px 80px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;
  animation: ${fadeUp} 0.4s ease both;
`;

const HeaderLeft = styled.div``;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;

  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #22c55e;
    animation: ${pulse} 2s ease infinite;
  }
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.4px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  animation: ${fadeUp} 0.4s ease 0.05s both;

  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
    border-radius: 12px 12px 0 0;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 6px;
  }

  .stat-value {
    font-size: 24px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.5px;
    line-height: 1;
  }

  .stat-sub {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
  }

  .stat-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 22px;
    opacity: 0.18;
  }
`;

// ─── Filter Bar ───────────────────────────────────────────────────────────────

const FilterBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  animation: ${fadeUp} 0.4s ease 0.1s both;
`;

const FilterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #94a3b8;
  white-space: nowrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  background: ${({ $active, $bg }) => $active ? $bg : "transparent"};
  color: ${({ $active, $color }) => $active ? $color : "#64748b"};

  &:hover {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 180px;
  padding: 7px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;

  &:focus {
    border-color: #2563eb;
    background: white;
    box-shadow: 0 0 0 3px #2563eb18;
  }
  &::placeholder { color: #cbd5e1; }
`;

const UrgencyFilter = styled.div`
  display: flex;
  gap: 6px;
  padding-left: 10px;
  border-left: 1px solid #e2e8f0;
`;

// ─── Request Grid ─────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
  animation: ${fadeUp} 0.4s ease 0.15s both;
`;

const RequestCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1.5px solid ${({ $urgency }) => $urgency === "High Alert" ? "#fca5a5" : "#e2e8f0"};
  padding: 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 40, 300)}ms;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px ${({ $color }) => $color}22;
    border-color: ${({ $color }) => $color}66;
  }

  ${({ $urgency }) => $urgency === "High Alert" && css`
    background: linear-gradient(135deg, white 80%, #fff5f5);
  `}
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const TypeTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const UrgencyDot = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};

  .dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot};
    ${({ $urgency }) => $urgency === "High Alert" && css`
      animation: ${pulse} 1s ease infinite;
    `}
  }
`;

const CardField = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 13px;

  .lbl {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    min-width: 56px;
  }

  .val {
    color: #334155;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .val-mono {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: #475569;
  }
`;

const CostBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-family: 'DM Mono', monospace;
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 64px 24px;
  color: #94a3b8;

  .icon { font-size: 40px; margin-bottom: 12px; opacity: 0.4; }
  .msg  { font-size: 15px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 13px; margin-top: 4px; }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease both;
`;

const Modal = styled.div`
  background: white;
  border-radius: 18px;
  width: min(520px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.2);
  animation: ${slideRight} 0.25s ease both;
`;

const ModalHeader = styled.div`
  padding: 22px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .icon-wrap {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: ${({ $bg }) => $bg};
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  span {
    font-size: 12px;
    color: #94a3b8;
  }
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: all 0.12s;
  &:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const ModalInput = styled.input`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  width: 100%;
  transition: all 0.15s;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}18;
  }
  &::placeholder { color: #cbd5e1; }
`;

const ModalSelect = styled.select`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
  color: #0f172a;
  width: 100%;
  appearance: none;
  padding-right: 30px;
  transition: all 0.15s;
  cursor: pointer;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background-color: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}18;
  }
`;

const UrgencyToggle = styled.div`
  display: flex;
  gap: 8px;
`;

const UrgencyBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  background: ${({ $active, $bg }) => $active ? $bg : "#f8fafc"};
  color: ${({ $active, $color }) => $active ? $color : "#94a3b8"};

  .dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${({ $active, $dot }) => $active ? $dot : "#cbd5e1"};
    ${({ $active, $value }) => $active && $value === "High Alert" && css`
      animation: ${pulse} 1s ease infinite;
    `}
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 22px;
  display: flex;
  gap: 10px;
  border-top: 1px solid #f1f5f9;
`;

const IssueBtn = styled.button`
  flex: 1;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: #059669;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover { background: #047857; transform: translateY(-1px); box-shadow: 0 6px 20px #05996944; }
  &:active { transform: none; }
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #64748b;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #e2e8f0; color: #0f172a; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function Issuance() {
  
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [filterUrgency, setFilterUrgency] = useState("All");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "IssuanceRequest"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const pending = data.filter((r) => r.status === "PENDING");
      setRequests(pending);
    });
    return () => unsub();
  }, []);

  const handleChange = (field, value) =>
    setSelectedRequest({ ...selectedRequest, [field]: value });

const today = new Date().toISOString().split("T")[0];
  const user    = auth.currentUser;
const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
};


// const form: {
//     agent: string;
//     folderNo: string;
//     legs: string;
//     cost: string;
//     supplier: string;
//     date: string;
//     issuedBy: string;
//     remarks: string;
// }

  const handleMarkIssued = async () => {

    
  if (!selectedRequest) return;
  const req = selectedRequest;
  setSaving(true);

  try {
    switch (req.type) {
      case "Flight":
        await addDoc(collection(db, "flightSupplierLedger"), {
          agent: req.agent || "-",
          folderNo: req.folderNo || "-",
          pnr: req.ref || "-",
          cost: parseFloat(req.cost || 0),
          supplier: req.supplier || "-",
          date: today,
          travelDate: req.travelDate,
          issuedBy: getDisplayName(user?.email),
          type: "NEW",
          remarks: req.ticketorder || "",
          timestamp: Timestamp.now(),
        });
        break;
      case "Hotel":
          await addDoc(collection(db, "hotelSupplierLedger"), {
                agent: req.agent || "-",
                folderNo: req.folderNo || "-",
                bookingRef: req.ref || "-",
                cost: parseFloat(req.cost || 0),
                supplier: req.supplier || "-",
                checkInDate: today,
                cancellationDate: req.cancellationDate,
                issuedBy: getDisplayName(user?.email),
                remarks: req.ticketorder || "",
                timestamp:  Timestamp.now(),
              });
        break;

      case "Transport":
          await addDoc(collection(db, "transportSupplierLedger"), {
                agent: req.agent || "-",
                folderNo: req.folderNo || "-",
                supplier: req.supplier || "-",
                date: today,
                issuedBy: getDisplayName(user?.email),
                remarks: req.ticketorder || "",
                cost: parseFloat(req.cost || 0),
                legs:  req.ref || "-",
                timestamp: Timestamp.now(),
              });
        break;

      case "Visa":
         await addDoc(collection(db, "visaSupplierLedger"), {
                agent: req.agent || "-",
                folderNo: req.folderNo || "-",
                type:req.ref || "-",
                supplier: req.supplier || "-",
                date: today,
                issuedBy: getDisplayName(user?.email),
                remarks: req.ticketorder || "",
                cost: parseFloat(req.cost || 0),
                timestamp: Timestamp.now(),
              });
        break;

      default:
        throw new Error(`Unknown request type: ${req.type}`);
    }

    await updateDoc(doc(db, "IssuanceRequest", req.id), {
      status: "ISSUED",
      agent:getDisplayName(req.agent),
      cost: parseFloat(req.cost),
      ticketorder : req.ticketorder,
      supplier :req.supplier,
      ref:req.ref,
      folderNo:req.folderNo,

    });

    enqueueSnackbar("Request marked as ISSUED ✓", { variant: "success" });
    setSelectedRequest(null);

  } catch (err) {
    console.error("Error marking request as issued:", err);
    enqueueSnackbar("Error updating request", { variant: "error" });
  } finally {
    setSaving(false);
  }
};

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filtered = requests.filter((r) => {
    const matchType = filterType === "All" || r.type === filterType;
    const matchUrgency = filterUrgency === "All" || r.urgency === filterUrgency;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.folderNo || "").toLowerCase().includes(q) ||
      (r.ref || "").toLowerCase().includes(q) ||
      (r.agent || "").toLowerCase().includes(q) ||
      (r.supplier || "").toLowerCase().includes(q);
    return matchType && matchUrgency && matchSearch;
  });

  // ── Stats ───────────────────────────────────────────────────────────────────

  const totalCost = requests.reduce((s, r) => s + (parseFloat(r.cost) || 0), 0);
  const highAlerts = requests.filter((r) => r.urgency === "High Alert").length;
  const typeCount = (t) => requests.filter((r) => r.type === t).length;

  const tc = selectedRequest ? (TYPES[selectedRequest.type] || TYPES.Flight) : null;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          {/* ── Header ── */}
          <Header>
            <HeaderLeft>
              <Badge>Live Queue</Badge>
              <PageTitle>Issuance Dashboard</PageTitle>
              <PageSub>Review and process pending issuance requests</PageSub>
            </HeaderLeft>
          </Header>

          {/* ── Stats ── */}
          <StatsRow>
            <StatCard $color="#f59e0b">
              <div className="stat-label">Pending</div>
              <div className="stat-value">{requests.length}</div>
              <div className="stat-sub">awaiting action</div>
              <div className="stat-icon">⏳</div>
            </StatCard>

            <StatCard $color="#ef4444">
              <div className="stat-label">High Alert</div>
              <div className="stat-value">{highAlerts}</div>
              <div className="stat-sub">urgent requests</div>
              <div className="stat-icon">🚨</div>
            </StatCard>

            <StatCard $color="#2563eb">
              <div className="stat-label">Total Value</div>
              <div className="stat-value">£{totalCost.toLocaleString()}</div>
              <div className="stat-sub">across all pending</div>
              <div className="stat-icon">💷</div>
            </StatCard>

            <StatCard $color="#7c3aed">
              <div className="stat-label">Filtered</div>
              <div className="stat-value">{filtered.length}</div>
              <div className="stat-sub">matching view</div>
              <div className="stat-icon">🔍</div>
            </StatCard>
          </StatsRow>

          {/* ── Filter Bar ── */}
          <FilterBar>
            <FilterLabel>Type</FilterLabel>
            <FilterGroup>
              {["All", ...Object.keys(TYPES)].map((t) => {
                const cfg = TYPES[t];
                return (
                  <FilterChip
                    key={t}
                    $active={filterType === t}
                    $color={cfg?.color || "#64748b"}
                    $bg={cfg?.bg || "#f1f5f9"}
                    onClick={() => setFilterType(t)}
                  >
                    {cfg?.icon} {t}
                    {t !== "All" && (
                      <span style={{ opacity: 0.6, fontSize: 10 }}>({typeCount(t)})</span>
                    )}
                  </FilterChip>
                );
              })}
            </FilterGroup>

            <UrgencyFilter>
              {["All", "Easy", "High Alert"].map((u) => {
                const cfg = URGENCY[u];
                return (
                  <FilterChip
                    key={u}
                    $active={filterUrgency === u}
                    $color={cfg?.color || "#64748b"}
                    $bg={cfg?.bg || "#f1f5f9"}
                    onClick={() => setFilterUrgency(u)}
                  >
                    {u}
                  </FilterChip>
                );
              })}
            </UrgencyFilter>

            <SearchInput
              placeholder="Search folder, ref, agent, supplier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterBar>

          {/* ── Request Cards ── */}
          <Grid>
            {filtered.length === 0 && (
              <EmptyState>
                <div className="icon">📭</div>
                <div className="msg">No pending requests</div>
                <div className="sub">
                  {search || filterType !== "All" || filterUrgency !== "All"
                    ? "Try adjusting your filters"
                    : "All caught up — check back soon"}
                </div>
              </EmptyState>
            )}

            {filtered.map((req, i) => {
              const cfg = TYPES[req.type] || TYPES.Flight;
              const urg = URGENCY[req.urgency] || URGENCY.Easy;
              return (
                <RequestCard
                  key={req.id}
                  $color={cfg.color} $urgency={req.urgency} $i={i}
                  onClick={() => setSelectedRequest(req)}
                >
                  <CardTop>
                    <TypeTag $color={cfg.color} $bg={cfg.bg}>
                      {cfg.icon} {req.type}
                    </TypeTag>
                    <UrgencyDot $color={urg.color} $dot={urg.dot} $urgency={req.urgency}>
                      <div className="dot" />
                      {req.urgency}
                    </UrgencyDot>
                  </CardTop>

                  <CardField>
                    <span className="lbl">Folder</span>
                    <span className="val-mono">#{req.folderNo}</span>
                  </CardField>
                  <CardField>
                    <span className="lbl">Ref</span>
                    <span className="val">{req.ref}</span>
                  </CardField>
                  <CardField>
                    <span className="lbl">Agent</span>
                    <span className="val">{req.agent}</span>
                  </CardField>
                  <CardField>
                    <span className="lbl">Supplier</span>
                    <span className="val">{req.supplier}</span>
                  </CardField>

                  <CostBadge>£{(parseFloat(req.cost) || 0).toLocaleString()}</CostBadge>
                </RequestCard>
              );
            })}
          </Grid>

          {/* ── Edit Modal ── */}
          {selectedRequest && tc && (
            <Overlay onClick={() => setSelectedRequest(null)}>
              <Modal onClick={(e) => e.stopPropagation()}>

                <ModalHeader>
                  <ModalTitle $bg={tc.bg}>
                    <div className="icon-wrap">{tc.icon}</div>
                    <div>
                      <h3>Edit Request</h3>
                      <span>{selectedRequest.type} · #{selectedRequest.folderNo}</span>
                    </div>
                  </ModalTitle>
                  <CloseBtn onClick={() => setSelectedRequest(null)}>×</CloseBtn>
                </ModalHeader>

                <ModalBody>
                  <FieldGrid>
                    <Field>
                      <label>Folder No</label>
                      <ModalInput
                        placeholder="Folder No"
                        value={selectedRequest.folderNo}
                        onChange={(e) => handleChange("folderNo", e.target.value)}
                        $color={tc.color}
                      />
                    </Field>

                    <Field>
                      {selectedRequest.type === "Transport"
                      &&<label>Legs</label> }
                      {selectedRequest.type === "Flight"
                      &&<label>PNR</label> }
                       {selectedRequest.type === "Visa"
                      &&<label>Visa Type</label> }
                       {selectedRequest.type === "Hotel"
                      &&<label>Reference</label> }
                      <ModalInput
                        placeholder="Reference"
                        value={selectedRequest.ref}
                        onChange={(e) => handleChange("ref", e.target.value)}
                        $color={tc.color}
                      />
                    </Field>

                    <Field>
                      <label>Agent</label>
                      <ModalInput
                        placeholder="Agent"
                        value={selectedRequest.agent}
                        onChange={(e) => handleChange("agent", e.target.value)}
                        $color={tc.color}
                      />
                    </Field>

                    <Field>
                      <label>Cost (£)</label>
                      <ModalInput
                        type="number"
                        placeholder="0.00"
                        value={selectedRequest.cost}
                        onChange={(e) => handleChange("cost", e.target.value)}
                        $color={tc.color}
                      />
                    </Field>


{selectedRequest.type === "Hotel"

&&
<Field>
  <label>cancellation</label>
  <ModalInput
    type="date"
    placeholder="Select a date"
    value={selectedRequest.cancellationDate}  // make sure this key exists in your state
    onChange={(e) => handleChange("cancellationDate", e.target.value)}
    $color={tc.color}
  />
</Field>}

{selectedRequest.type === "Flight"

&&
<Field>
  <label>Travel Date</label>
  <ModalInput
    type="date"
    placeholder="Select a date"
    value={selectedRequest.travelDate}  // make sure this key exists in your state
    onChange={(e) => handleChange("travelDate", e.target.value)}
    $color={tc.color}
  />
</Field>}
                    

                    <Field >
                      <label>Supplier</label>
                      <ModalSelect
                        value={selectedRequest.supplier}
                        onChange={(e) => handleChange("supplier", e.target.value)}
                        $color={tc.color}
                      >
                        <option value="">Select supplier</option>
                        {/* {SUPPLIERS.map((s) => <option key={s} value={s}>{s}</option>)} */}

                        {getSupplierList(selectedRequest.type).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </ModalSelect>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <label>Urgency</label>
                      <UrgencyToggle>
                        {Object.entries(URGENCY).map(([val, cfg]) => (
                          <UrgencyBtn
                            key={val}
                            $active={selectedRequest.urgency === val}
                            $color={cfg.color} $bg={cfg.bg} $dot={cfg.dot} $value={val}
                            onClick={() => handleChange("urgency", val)}
                          >
                            <span className="dot" />
                            {val}
                          </UrgencyBtn>
                        ))}
                      </UrgencyToggle>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <label>Ticket Order</label>
                      <textarea
                        placeholder=""
                        value={selectedRequest.ticketorder}
                        onChange={(e) => handleChange("ticketorder", e.target.value)}
                        $color={tc.color}
                        style={{
                          width: "100%",
                          minHeight: "250px",
                          marginTop: "10px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          resize: "vertical"
                        }}
                      />

                    </Field>

                  </FieldGrid>
                </ModalBody>

                <ModalFooter>
                  <IssueBtn onClick={handleMarkIssued} disabled={saving}>
                    {saving ? "Saving…" : "✓ Mark as Issued"}
                  </IssueBtn>
                  <CancelBtn onClick={() => setSelectedRequest(null)}>Cancel</CancelBtn>
                </ModalFooter>

              </Modal>
            </Overlay>
          )}

        </Inner>
      </Page>
    </>
  );
}